import React, { useEffect, useState } from 'react';
import '../../styled/pages/dashboard.scss';
import { Header, Loader, Success, Failed } from '../../components';
import { customStyles } from '../../Utils/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { editPacs } from '../../rtk-apis/pacs/editPacs';
import { PacsValidationSchema } from '../auth/helper/Schema';
import { addPacs } from '../../rtk-apis/pacs/addPacs';
import BackButton from '../../components/BackButton';
import BreadCrumbs from '../../components/Breadcrumbs';
import { logoutAndWipeBrowser } from '../../Utils/authUtils';

const AddUpdatePacs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { userDetails } = location?.state || {};
  const hospitalId = sessionStorage?.getItem('hospitalId');

  const [displayMessage, setDisplayMessage] = useState('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitTriggered, setIsSubmitTriggered] = useState(false);

  const getPrefilledLocalURL = () => {
    if (userDetails?.localURL) return userDetails.localURL;
    if (userDetails?.localUrl) return userDetails.localUrl;
    try {
      let overrideStr: string | null = null;
      if (typeof document !== 'undefined' && document.cookie) {
        const m = document.cookie.match(/(?:^|; )pacs_override=([^;]*)/);
        if (m) overrideStr = decodeURIComponent(m[1]);
      }
      if (!overrideStr && typeof localStorage !== 'undefined') {
        overrideStr = localStorage.getItem('pacs_override');
      }
      if (!overrideStr && typeof sessionStorage !== 'undefined') {
        overrideStr = sessionStorage.getItem('pacs_override');
      }
      if (overrideStr) {
        const ov = JSON.parse(overrideStr);
        if (ov?.localURL) return ov.localURL;
        if (ov?.localUrl) return ov.localUrl;
      }
      let detailsStr: string | null = null;
      if (typeof sessionStorage !== 'undefined') {
        detailsStr = sessionStorage.getItem('pacsDetails');
      }
      if (!detailsStr && typeof localStorage !== 'undefined') {
        detailsStr = localStorage.getItem('pacsDetails');
      }
      if (detailsStr) {
        const det = JSON.parse(detailsStr);
        if (det) {
          if (Array.isArray(det)) {
            const d0 = det[0] || {};
            return d0.localURL || d0.localUrl || '';
          }
          return det.localURL || det.localUrl || '';
        }
      }
    } catch (e) {}
    return '';
  };

  const {
    status: editStatus,
    result: editResult,
    msg: editMsg,
    loading: editLoading,
  } = useSelector((state: RootState) => state.editPacsDataResponse);
  const {
    status: addStatus,
    result: addResult,
    msg: addMsg,
    loading: addLoading,
  } = useSelector((state: RootState) => state.addPacsResponse);

  const initialValues = {
    _id: userDetails?._id ?? '',
    aeTitle: userDetails?.aeTitle ?? '',
    ipAddress: userDetails?.ipAddress ?? 'default',
    port: userDetails?.port ?? '0000',
    localURL: getPrefilledLocalURL(),
    isLocalPacs:
      userDetails?.isLocalPacs ??
      (userDetails?.ipAddress && userDetails?.ipAddress !== 'default' ? true : false),
    hospitalId: hospitalId ?? '',
  };

  // Clear previous states when component mounts or userDetails change
  useEffect(() => {
    // Reset all relevant states
    setDisplayMessage('');
    setIsSuccessModalOpen(false);
    setIsFailedModalOpen(false);
    setIsSubmitTriggered(false);

    // Determine edit mode
    if (userDetails) {
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
    }

    // Clear previous API states
    dispatch({ type: 'editPacsDataResponse/clearState' });
    dispatch({ type: 'addPacsResponse/clearState' });
  }, [userDetails, dispatch]);

  const [submittedValues, setSubmittedValues] = useState(null);

  const handleSubmit = (values, { setSubmitting }) => {
    setIsSubmitTriggered(true);
    setSubmittedValues(values);
    const payload = {
      aeTitle: values.aeTitle,
      ipAddress: values.isLocalPacs ? values.ipAddress : 'default',
      port: values.isLocalPacs ? values.port : '0000',
      isLocalPacs: values.isLocalPacs,
      localURL: values?.localURL ? values.localURL : 'http://localhost:8080',
    };
    if (isEditMode) {
      dispatch(
        editPacs({
          ...payload,
          id: userDetails?._id,
        })
      );
    } else {
      dispatch(
        addPacs({
          ...payload,
          hospitalId: hospitalId,
        })
      );
    }
    setSubmitting(false);
  };

  const Logout = () => {
    void logoutAndWipeBrowser(dispatch);
  };

  const handleSuccess = message => {
    setDisplayMessage(message);
    setIsSuccessModalOpen(true);
    setTimeout(() => {
      try {
        const override = submittedValues?.isLocalPacs
          ? {
              ipAddress: submittedValues?.ipAddress,
              port: submittedValues?.port,
              aeTitle: submittedValues?.aeTitle,
              localURL: submittedValues?.localURL,
            }
          : {
              ipAddress: 'default',
              port: '0000',
              aeTitle: submittedValues?.aeTitle,
              localURL: '',
            };
        document.cookie = `pacs_override=${encodeURIComponent(
          JSON.stringify(override)
        )}; path=/; max-age=31536000`;
        try {
          localStorage.setItem('pacs_override', JSON.stringify(override));
        } catch (e) {}
      } catch (e) {}
      setIsSuccessModalOpen(false);
      setDisplayMessage('');
      Logout();
    }, 1500);
  };

  useEffect(() => {
    if (isSubmitTriggered) {
      if (isEditMode && editStatus === 'SUCCESS') {
        handleSuccess(editMsg + ' Please relogin.');
        setIsSubmitTriggered(false);
      } else if (!isEditMode && addStatus === 'SUCCESS') {
        handleSuccess(addMsg + ' Please relogin.');
        setIsSubmitTriggered(false);
      }
    }
  }, [isSubmitTriggered, isEditMode, editStatus, addStatus, editMsg, addMsg]);

  const breadcrumbs = [
    { label: 'Manage PACS', link: '/pacs', icon: 'sidebar-icon' },
    { label: `${isEditMode ? 'Edit' : 'Add'} PACS` },
  ];

  return (
    <section
      className="layoutContainer"
      style={customStyles.layoutBackground}
    >
      <div className="layoutGrid">
        <div className="page">
          <Header />
          <div className="page__body">
            <Formik
              key={isEditMode ? 'edit-mode' : 'add-mode'} // Force re-render when mode changes
              initialValues={initialValues}
              validationSchema={PacsValidationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ isSubmitting, values, setFieldValue }) => (
                <Form className="form">
                  <div className="page__body__head">
                    <BackButton />
                    <BreadCrumbs breadcrumbs={breadcrumbs} />
                    <div className="btn__group">
                      <button
                        className="btn btn__white btn__outlined"
                        type="button"
                        onClick={() => navigate('/pacs')}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn__primary"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isEditMode ? 'Update' : 'Save'}
                      </button>
                    </div>
                  </div>
                  <section className="form__section">
                    <h1 className="form__section__title">PACS Details</h1>
                    <Field
                      type="hidden"
                      name="_id"
                    />
                    <div className="formRow formRow__2">
                      <div className="formCol">
                        <div
                          className="form__group"
                          style={{
                            display: 'flex',
                            alignItems: 'baseline',
                          }}
                        >
                          <label
                            htmlFor="isLocalPacs"
                            className="form__label"
                            style={{ marginRight: '1rem' }}
                          >
                            Use Local PACS
                          </label>
                          <div>
                            <Field
                              type="checkbox"
                              id="isLocalPacs"
                              name="isLocalPacs"
                              onChange={e => {
                                const checked = e?.target?.checked;
                                setFieldValue('isLocalPacs', checked);
                                if (checked) {
                                  if (values?.ipAddress === 'default' || !values?.ipAddress) {
                                    setFieldValue('ipAddress', 'localhost');
                                  }
                                  if (values?.port === '0000' || !values?.port) {
                                    setFieldValue('port', '8080');
                                  }
                                  if (!values?.localURL) {
                                    const v = getPrefilledLocalURL();
                                    setFieldValue('localURL', v || 'http://localhost:8080');
                                  }
                                } else {
                                  setFieldValue('ipAddress', 'default');
                                  setFieldValue('port', '0000');
                                  setFieldValue('localURL', '');
                                }
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="formRow formRow__2">
                      <div className="formCol">
                        <div className="form__group">
                          <label
                            htmlFor="aeTitle"
                            className="form__label"
                          >
                            AE Title
                            <sup className="form__label__asterisk">*</sup>
                          </label>
                          <Field
                            type="text"
                            id="aeTitle"
                            name="aeTitle"
                            className="form__input form__input--sm"
                            placeholder="AE Title"
                          />
                          <ErrorMessage
                            name="aeTitle"
                            component="div"
                            className="form__error"
                          />
                        </div>
                      </div>
                      {values?.isLocalPacs && (
                        <div className="formCol">
                          <div className="form__group">
                            <label
                              htmlFor="ipAddress"
                              className="form__label"
                            >
                              IP Address
                              <sup className="form__label__asterisk">*</sup>
                            </label>
                            <Field
                              type="text"
                              id="ipAddress"
                              name="ipAddress"
                              className="form__input form__input--sm"
                              placeholder="IP Address"
                            />
                            <ErrorMessage
                              name="ipAddress"
                              component="div"
                              className="form__error"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    {values?.isLocalPacs && (
                      <div className="formRow formRow__2">
                        <div className="formCol">
                          <div className="form__group">
                            <label
                              htmlFor="port"
                              className="form__label"
                            >
                              Port
                              <sup className="form__label__asterisk">*</sup>
                            </label>
                            <Field
                              type="text"
                              id="port"
                              name="port"
                              className="form__input form__input--sm"
                              placeholder="Port"
                            />
                            <ErrorMessage
                              name="port"
                              component="div"
                              className="form__error"
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__group">
                            <label
                              htmlFor="localURL"
                              className="form__label"
                            >
                              Local URL
                              <sup className="form__label__asterisk">*</sup>
                            </label>
                            <Field
                              type="text"
                              id="localURL"
                              name="localURL"
                              className="form__input form__input--sm"
                              placeholder="http://localhost:8080"
                            />
                            <ErrorMessage
                              name="localURL"
                              component="div"
                              className="form__error"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </section>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
      <Loader isLoading={editLoading || addLoading} />
      <Success
        handleCloseModal={() => setIsSuccessModalOpen(false)}
        title={displayMessage}
        isSuccessModalOpen={isSuccessModalOpen}
      />
      <Failed
        handleCloseModal={() => setIsFailedModalOpen(false)}
        title={displayMessage}
        isFailedModalOpen={isFailedModalOpen}
      />
    </section>
  );
};

export default AddUpdatePacs;
